import { Action } from 'redux';
import { Rectangle } from 'ark-common';
import { PointerInfo, RawPointerEvent } from '../../../../common/src/ts/types/uievent';
import { isTouchEvent, isPointerEvent } from '../../../../common/src/ts/utils/uievent';

// Global view action

export const VIEW_SET_TAB_PANEL_AREA_RECT = 'VIEW_SET_TAB_PANEL_AREA_RECT';
export const VIEW_SET_POPUP_RECT = 'VIEW_SET_POPUP_RECT';

export const VIEW_ANIMATION_FRAME = 'VIEW_ANIMATION_FRAME';

// Per-tab view actions

export const VIEW_ENSURE_STATE = 'VIEW_ENSURE_STATE';

export const VIEW_ZOOM_IN = 'VIEW_ZOOM_IN';
export const VIEW_ZOOM_OUT = 'VIEW_ZOOM_OUT';
export const VIEW_SET_ZOOM = 'VIEW_SET_ZOOM';
export const VIEW_RESET_ZOOM = 'VIEW_RESET_ZOOM';
export const VIEW_ZOOM_REGION = 'VIEW_ZOOM_REGION';

export const VIEW_SET_PAN = 'VIEW_SET_PAN';

export const VIEW_POINTER_DOWN = 'VIEW_POINTER_DOWN';
export const VIEW_POINTER_UP = 'VIEW_POINTER_UP';
export const VIEW_POINTER_MOVE = 'VIEW_POINTER_MOVE';

export const VIEW_GESTURE_START = 'VIEW_GESTURE_START';
export const VIEW_GESTURE_CHANGE = 'VIEW_GESTURE_CHANGE';
export const VIEW_GESTURE_END = 'VIEW_GESTURE_END';

export const VIEW_SET_CONTENT_SIZE = 'VIEW_SET_CONTENT_SIZE';

export interface SetRectAction extends Action {
  readonly type: typeof VIEW_SET_TAB_PANEL_AREA_RECT;
  readonly rect: Rectangle;
}

export const setTabPanelAreaRect = (rect: Rectangle): SetRectAction => ({
  rect, type: VIEW_SET_TAB_PANEL_AREA_RECT,
});

export interface SetPopupRectAction extends Action {
  readonly type: typeof VIEW_SET_POPUP_RECT;
  readonly id: string;
  readonly rect: Rectangle;
}

// popupのコンテンツ表示領域のrectを設定 (タイトルバー等は含まない)
export const setPopupRect = (id: string, rect: Rectangle): SetPopupRectAction => ({
  id, rect, type: VIEW_SET_POPUP_RECT,
});

export interface AnimationFrameAction extends Action {
  type: typeof VIEW_ANIMATION_FRAME;
  timestamp: number;
}

export const animationFrame = (): AnimationFrameAction => ({
  type: VIEW_ANIMATION_FRAME,
  timestamp: Date.now(),
});

export interface SimpleViewAction extends Action {
  readonly type: (
    typeof VIEW_ENSURE_STATE
    | typeof VIEW_ZOOM_IN
    | typeof VIEW_ZOOM_OUT
    | typeof VIEW_RESET_ZOOM
  );
  readonly activePanel: string;
}

export const ensureState = (activePanel: string): SimpleViewAction => ({
  activePanel,
  type: VIEW_ENSURE_STATE,
});

export const zoomIn = (activePanel: string): SimpleViewAction => ({
  activePanel,
  type: VIEW_ZOOM_IN,
});

export const zoomOut = (activePanel: string): SimpleViewAction => ({
  activePanel,
  type: VIEW_ZOOM_OUT,
});

export const resetZoom = (activePanel: string): SimpleViewAction => ({
  activePanel,
  type: VIEW_RESET_ZOOM,
});

export interface SetZoomAction extends Action {
  readonly type: typeof VIEW_SET_ZOOM;
  readonly activePanel: string;
  readonly zoom: number;
}

export const setZoom = (activePanel: string, zoom: number): SetZoomAction => ({
  activePanel,
  zoom,
  type: VIEW_SET_ZOOM,
});

export interface ZoomRegionAction extends Action {
  readonly type: typeof VIEW_ZOOM_REGION;
  readonly activePanel: string;
  readonly region: Rectangle;
}

export const zoomRegion = (activePanel: string, region: Rectangle): ZoomRegionAction => ({
  activePanel,
  region,
  type: VIEW_ZOOM_REGION,
});

export interface SetPanAction extends Action {
  readonly type: typeof VIEW_SET_PAN;
  readonly activePanel: string;
  readonly x: number;
  readonly y: number;
}

export const setPan = (activePanel: string, x: number, y: number): SetPanAction => ({
  activePanel,
  x,
  y,
  type: VIEW_SET_PAN,
});

type POINTER_TYPES = typeof VIEW_POINTER_DOWN | typeof VIEW_POINTER_UP | typeof VIEW_POINTER_MOVE;

export interface PointerAction extends Action {
  readonly type: POINTER_TYPES;
  readonly activePanel: string;
  readonly pointer: PointerInfo;
  readonly timestamp: number;
}

const getPointerPosition = (ev: MouseEvent | PointerEvent) => {
  let { screenX, screenY } = ev;
  const windowPosition = { x: window.screenX, y: window.screenY };

  const isMS = (screen as any).systemXDPI !== undefined;
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edge/.test(navigator.userAgent);

  if (isMS) {
    // IE/Edgeのみ, screenXがCSSピクセルでなく物理ピクセルなので回避
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/16006191/
    screenX /= window.devicePixelRatio;
    screenY /= window.devicePixelRatio;
  }

  if (isChrome) {
    // Chromeのみ, Touch時のscreenXがウィンドウ起点を返すバグがあるので回避
    // https://bugs.chromium.org/p/chromium/issues/detail?id=813464
    if (isPointerEvent(ev) && ev.pointerType === 'touch') {
      windowPosition.x = 0;
      windowPosition.y = 0;
    }
  }

  return {
    windowX: screenX - windowPosition.x,
    windowY: screenY - windowPosition.y,
  };
};

export const makePointerAction =
  (type: POINTER_TYPES, activePanel: string, ev: RawPointerEvent): PointerAction => {
    const timestamp = Date.now();

    if (isTouchEvent(ev)) {
      // 2つ目以降のTouchは無視する. マルチタッチ操作は、PointerEvent及びGestureEventで行う.
      let windowX = 0;
      let windowY = 0;
      if (ev.touches.length > 0) { // Check for touchend
        windowX = ev.touches[0].screenX - window.screenX;
        windowY = ev.touches[0].screenY - window.screenY;
      }
      return {
        type,
        activePanel,
        timestamp,
        pointer: { windowX, windowY, pointerId: 0 },
      };
    }

    // MouseEvent or PointerEvent
    const pointerId = (isPointerEvent(ev) && ev.isPrimary ? 0 : 1); // 2以上のIDは強引に丸める
    return {
      type,
      activePanel,
      timestamp,
      pointer: { pointerId, ...getPointerPosition(ev) },
    };
  };

type GESTURE_TYPES =
  typeof VIEW_GESTURE_START | typeof VIEW_GESTURE_CHANGE | typeof VIEW_GESTURE_END;

export interface GestureAction extends Action {
  type: GESTURE_TYPES;
  activePanel: string;
  scale: number;
  screenX: number;
  screenY: number;
}

const gesture = (activePanel: string, type: GESTURE_TYPES, ev: GestureEvent): GestureAction => ({
  activePanel,
  type,
  scale: ev.scale,
  screenX: ev.screenX,
  screenY: ev.screenY,
});

export const gestureStart = (activePanel: string, ev: GestureEvent): GestureAction =>
  gesture(activePanel, VIEW_GESTURE_START, ev);
export const gestureChange = (activePanel: string, ev: GestureEvent): GestureAction =>
  gesture(activePanel, VIEW_GESTURE_CHANGE, ev);
export const gestureEnd = (activePanel: string, ev: GestureEvent): GestureAction =>
  gesture(activePanel, VIEW_GESTURE_END, ev);

export interface SetContentSizeAction extends Action {
  readonly type: typeof VIEW_SET_CONTENT_SIZE;
  readonly panelID: string;
  readonly width: number;
  readonly height: number;
}

export const setContentSize =
  (panelID: string, width: number, height: number): SetContentSizeAction => ({
    panelID,
    width,
    height,
    type: VIEW_SET_CONTENT_SIZE,
  });

export type GeneralViewAction = SetRectAction | AnimationFrameAction;
export type PerTabViewAction = (
  SimpleViewAction | SetZoomAction | SetPanAction |
  ZoomRegionAction | PointerAction | GestureAction
);
export type ViewAction = (
  GeneralViewAction | PerTabViewAction | SetContentSizeAction | SetPopupRectAction
);

export const isViewAction = (action: Action): action is ViewAction => {
  switch (action.type) {
    case VIEW_SET_TAB_PANEL_AREA_RECT:
    case VIEW_SET_POPUP_RECT:
    case VIEW_ANIMATION_FRAME:
    case VIEW_ENSURE_STATE:
    case VIEW_ZOOM_IN:
    case VIEW_ZOOM_OUT:
    case VIEW_SET_ZOOM:
    case VIEW_RESET_ZOOM:
    case VIEW_ZOOM_REGION:
    case VIEW_SET_PAN:
    case VIEW_POINTER_DOWN:
    case VIEW_POINTER_UP:
    case VIEW_POINTER_MOVE:
    case VIEW_GESTURE_START:
    case VIEW_GESTURE_CHANGE:
    case VIEW_GESTURE_END:
    case VIEW_SET_CONTENT_SIZE:
      return true;
    default:
      return false;
  }
};
